import React, { useState, useMemo, useEffect, useRef } from 'react'
import {useQuery, useClient} from 'jsonapi-react'
import LeadError from '../LeadError'
import Checkbox from 'app/theme/forms/Checkbox'
import { Spinner } from 'app/theme/Spinner'
import { X } from 'react-feather'

export default function LeadCuisines(props) {
  const { data, setData, errors, setErrors } = props
  const [selectAll, setSelectAll] = useState(false)
  const [selectedCuisines, setSelectedCuisines] = useState(new Map())
  const [totalResults, setTotalResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const client = useClient()

  const tagQuery = useQuery(['tags', {
    filter: {
      active: true
    },
    page: {
      size: 999,
    },
    sort: 'name',
  }], {
    cacheTime: 60 * 10,
  })

  useEffect(() => {
    if (!data.address?.location?.lat || !data.cuisines?.length) {
      return setTotalResults(null)
    }
    setLoading(true)
    const filters = {
      active: true,
      closed: false,
      standing: ['good', 'risky'],
      tag_id: data.cuisines,
      service_area: {
        contains: JSON.stringify({...data.address.location, radius: 25})
      }
    }
    if (data.payer === 'host') {
      filters.private_events = true
    } else {
      filters.public_events = true
    }

    const lengthPromise = client.fetch(['caterers', {
      filter: filters,
      page: {
        size: 0
      },
      stats: {
        total: 'count',
      }
    }])

    lengthPromise.then((result, error) => {
      setLoading(false)
      setTotalResults(result.meta.stats.total.count)
    })
  }, [data.cuisines])

  // Updates on initial tagQuery.data load
  useEffect(() => {
    if (!tagQuery.data) return
    // Keeps state when user returns from next step
    if (data.cuisines?.length) {
      const selectedTags = data.cuisines?.map(id => {
        const tag = tagQuery.data.find(({ id: tagId }) => id === tagId)
        return tag ? [tag.id, tag.primary] : null
      }).filter(Boolean)

      setSelectedCuisines(new Map(selectedTags || []))
    }
  }, [tagQuery.data])

  // Cuisines failed to load, or loaded with nothing to offer
  const hasCuisineLoadError = tagQuery.error || (!tagQuery.isLoading && (!tagQuery.data || (tagQuery.data?.filter(tag => tag.primary) || []).length === 0))

  useEffect(() => {
    // There is nothing to pick from, so the lead has no cuisine preference to
    // give. null is the same "no cuisine filter" the form already sends when
    // every cuisine is selected — an empty list would block the step instead.
    if (hasCuisineLoadError) {
      return setData(prev => ({ ...prev, cuisines: null }))
    }

    const primaryTags = tagQuery.data?.filter(tag => tag.primary) || []
    const primarySelectedCuisines = [...selectedCuisines].filter(([id, primary]) => primary === true)
    const nonPrimarySelectedCuisines = [...selectedCuisines].filter(([id, primary]) => primary === false)
    const allSelected = primarySelectedCuisines.length === primaryTags.length && primarySelectedCuisines.length > 0 && primaryTags.length > 0
    const getSelectedCuisineIds = (selectedCuisines) => [...selectedCuisines].map(([id, primary]) => id)

    setSelectAll(allSelected)

    if (allSelected) {
      setData(prev => ({
        ...prev,
        cuisines: nonPrimarySelectedCuisines.length ? getSelectedCuisineIds(nonPrimarySelectedCuisines) : null,
      }))
    }
    else {
      setData(prev => ({
        ...prev,
        cuisines: getSelectedCuisineIds(selectedCuisines)
      }))
    }
  }, [selectedCuisines, hasCuisineLoadError])

  return (
    <div>
      {tagQuery.isLoading ? (
        <div className="">
          <Spinner className="text-blue w-10 h-10 mx-auto my-5"/>
        </div>
      ) : hasCuisineLoadError ? (
        <div className="font-light">
          <div className="px-8 py-6 text-center">
            <div className="text-red-600 mb-4">
              <svg className="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-semibold mb-2">Unable to Load Cuisines</h3>
              <p className="text-gray-600 mb-4">
                We're having trouble loading the cuisine options right now. This might be due to a temporary connection issue.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 font-medium">Suggestion:</p>
                <p className="text-blue-700">
                  Please try refreshing the page to reload the cuisine options,
                  or continue and we'll show you every cuisine available.
                </p>
              </div>
              <button 
                type="button"
                onClick={() => window.location.reload()}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors mr-3"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="font-light">
          <div
              className={[
                'px-8 py-3',
              ]}
            >
              <Checkbox
                autoFocus
                label='Select All'
                data-test-id='select-all-cuisines'
                checked={selectAll}
                onChange={checked => {
                  setErrors(false)
                  setSelectAll(checked)

                  const getSelectedNonPrimaryTags = prev => [...prev].filter(([id, primary]) => primary === false)

                  if (checked) {
                    const primaryTags = tagQuery.data?.filter(tag => tag.primary) || []
                    const primaryTagsIds = primaryTags.map(({id, primary}) => [id, primary])

                    setSelectedCuisines(prev => {
                      const selectedNonPrimaryTags = getSelectedNonPrimaryTags(prev)
                      return new Map([...primaryTagsIds, ...selectedNonPrimaryTags])
                    })
                  }
                  else {
                    setSelectedCuisines(prev => new Map(getSelectedNonPrimaryTags(prev)))
                  }
                }}
              />
            </div>
          {(tagQuery.data?.filter(t => t.primary) || []).map((tag, index) => (
            <div
              key={tag.id}
              className={[
                'px-8 py-3',
                'border-t border-gray-300',
              ]}
            >
              <Checkbox
                label={tag.name}
                data-test-id='cuisines'
                checked={selectedCuisines.has(tag.id)}
                onChange={checked => {
                  setErrors(false)

                  if (checked) {
                    setSelectedCuisines(prev =>  {
                      return new Map(prev.set(tag.id, tag.primary))
                    })
                  } else {
                    setSelectedCuisines(prev => {
                      prev.delete(tag.id)
                      return new Map(prev)
                    })
                  }
                }}
              />
            </div>
          ))}
          <div className="px-8 py-3 border-t border-gray-300">
            <CuisineAutoComplete
              tags={tagQuery.data?.filter(t => !t.primary) || []}
              cuisines={([...selectedCuisines] || []).filter(c => c[1] === false).map(c => c[0])}
              setCuisines={callback => {
                setSelectedCuisines(prev => { 
                  const ids = callback([...prev].map(c => c[0]))
                  const tags = ids.map(id => {
                    const tag = tagQuery.data?.find(({id: tagQueryId}) => tagQueryId === id)
                    return [id, tag?.primary]
                  })
                  return new Map(tags)
                })
              }}
            />
          </div>
          {errors?.cuisines && (
            <div className="px-8 py-3 border-t border-gray-300 font-bold text-center">
              <LeadError
                message={errors.cuisines}
              />
            </div>
          )}
        </div>
      )}
      {data.address.location?.lat ? (
        totalResults === null ? (
          <p className="block text-center py-2 border-t">Make a selection</p>
        ) : loading ? (
          <p className="block text-center py-2 border-t">Loading...</p>
        ) : totalResults > 0 ? (
          <p className="block text-center py-2 border-t">{totalResults} food trucks found</p>
        ) : (
          <p className="block text-center py-2 border-t">No food trucks available</p>
        )
      ) : null }
    </div>
  )
}

function CuisineAutoComplete(props) {
  const {
    tags = [],
    cuisines = [],
    setCuisines,
  } = props

  const [term, setTerm] = useState('')
  const [searchWidth, setSearchWidth] = useState()
  const [focused, setFocused] = useState(false)
  const [hovered, setHovered] = useState()

  const wrapperRef = useRef()
  const sizerRef = useRef()
  const inputRef = useRef()
  const optionsRef = useRef()

  const options = useMemo(() => {
    let options = tags

    if (cuisines.length) {
      options = options.filter(tag => cuisines.indexOf(tag.id) === -1)
    }

    if (term) {
      const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '')
      options = options.filter(tag => tag.name.match(new RegExp(escaped, 'i')))
    }

    return options
  }, [tags, term])

  useEffect(() => {
    if (focused) {
      inputRef.current.focus()
    } else {
      if (hovered) {
        setHovered(null)
      }
      if (term) {
        setTerm('')
      }
    }
  }, [focused])

  useEffect(() => {
    const onClick = event => {
      if (!wrapperRef.current.contains(event.target)) {
        setFocused(false)
      }
    }
    if (focused) {
      document.addEventListener('click', onClick)
      return () => {
        document.removeEventListener('click', onClick)
      }
    }
  }, [focused])

  return (
    <div
      ref={wrapperRef}
      className={[
        'relative input-basic bg-white p-0',
        focused && 'border-blue'
      ]}
      onClick={e => {
        if (!optionsRef.current || !optionsRef.current.contains(e.target)) {
          setFocused(prev => !prev)
        }
      }}
    >
      <div
        className={[
          'flex flex-wrap items-center overflow-hidden',
          cuisines.length && 'pt-1 pb-2 pl-1',
        ]}
      >
        {cuisines.map(id => (
          <div
            key={id}
            className={[
              'flex items-center bg-blue rounded-sm text-white',
              'pl-3 mt-1 ml-1',
            ]}
          >
            <span>
              {tags.find(t => t.id === String(id))?.name}
            </span>
            <button
              type="button"
              className="btn ml-1 p-1 hover:bg-blue-600"
              onMouseUp={e => {
                e.stopPropagation()
                setCuisines(prev => prev.filter(i => i !== id))
                inputRef.current.focus()
              }}
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <div className="flex flex-1">
          <input
            ref={inputRef}
            value={term}
            placeholder={cuisines.length ? undefined : 'Other'}
            style={{
              minWidth: searchWidth || (cuisines.length ? 1 : undefined),
            }}
            className={[
              'text-lg w-full border-0 bg-transparent focus:outline-none font-light',
              cuisines.length ? 'mt-1 ml-2' : 'px-3 py-2',
            ]}
            onKeyDown={event => {
              if ((event.keyCode !== 38 && event.keyCode !== 40) || options.length < 2) {
                return
              }

              let index = hovered ? options.indexOf(hovered) : 0

              if (event.keyCode === 38) {
                index = index - 1 < 0 ? options.length - 1 : index - 1
              } else if (event.keyCode === 40) {
                index = index + 1 > options.length - 1 ? 0 : index + 1
              }

              if (!options[index]) return

              setHovered(options[index])
              const target = optionsRef.current.querySelector(`div[data-tag-id="${options[index].id}"]`)

              if (target) {
                target.scrollIntoView({ behavior: 'auto', block: 'nearest' })
              }
            }}
            onChange={e => {
              const val = e.target.value
              sizerRef.current.innerHTML = val
              setSearchWidth(val ? Math.ceil(sizerRef.current.offsetWidth) + 2 : undefined)
              setTerm(val)
            }}
          />
          <span
            ref={sizerRef}
            className="text-lg absolute top-0 right-0 invisible h-0 whitespace-pre pointer-events-none"
          />
        </div>
      </div>
      {focused && (
        <div
          ref={optionsRef}
          style={{
            maxHeight: 258,
            left: -1,
            right: -1,
          }}
          className={[
            'bg-white shadow-xl rounded-sm absolute z-50 py-1 mt-1',
            'border border-gray-300 grid text-lg overflow-auto'
          ]}
        >
          {options.length ? options.map((tag, index) => (
            <div
              key={tag.id}
              data-tag-id={tag.id}
              className={[
                'px-3 leading-loose cursor-default',
                ((!hovered && index === 0) || (hovered && hovered.id === tag.id)) && 'bg-blue-200',
              ]}
              onMouseUp={() => {
                setCuisines(prev => prev.concat(tag.id))
                setHovered(null)
                setTerm('')
                inputRef.current.focus()
              }}
              onMouseOver={() => {
                setHovered(tag)
              }}
            >
              {tag.name}
            </div>
          )) : (
            <div
              className="px-3 py-2 leading-loose cursor-default text-center"
            >
              No options
            </div>
          )}
        </div>
      )}
    </div>
  )
}
